import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import type {Category, CategoryQueryParams, CreateCategoryCommand, UpdateCategoryCommand} from '@/types/category';
import {categoryService} from '@/services/categoryService';

interface CategoryState {
  items: Category[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

const initialState: CategoryState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  totalPages: 0,
  loading: false,
  error: null,
};

// 异步 action
export const fetchCategories = createAsyncThunk(
  'category/fetchCategories',
  async (params: CategoryQueryParams) => {
    const response = await categoryService.getCategoryList(params);
    return response;
  }
);

export const fetchCategoryTree = createAsyncThunk(
  'category/fetchCategoryTree',
  async () => {
    const response = await categoryService.getCategoryTree();
    return response;
  }
);

export const createCategory = createAsyncThunk(
  'category/createCategory',
  async (data: CreateCategoryCommand) => {
    const response = await categoryService.createCategory(data);
    return response;
  }
);

export const updateCategory = createAsyncThunk(
  'category/updateCategory',
  async (data: UpdateCategoryCommand) => {
    const response = await categoryService.updateCategory(data);
    return response;
  }
);

export const deleteCategory = createAsyncThunk(
  'category/deleteCategory',
  async (id: number) => {
    await categoryService.deleteCategory(id);
    return id;
  }
);

const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // 获取分类列表
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.page = action.payload.page;
        state.pageSize = action.payload.pageSize;
        state.totalPages = Math.ceil(action.payload.total / action.payload.pageSize);
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取分类列表失败';
      })
      // 获取分类树
      .addCase(fetchCategoryTree.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategoryTree.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
        state.total = action.payload.length;
        state.page = 1;
        state.pageSize = action.payload.length;
        state.totalPages = 1;
      })
      .addCase(fetchCategoryTree.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取分类树失败';
      })
      // 创建分类
      .addCase(createCategory.fulfilled, (state, action) => {
        state.items.push(action.payload);
        state.total += 1;
      })
      // 更新分类
      .addCase(updateCategory.fulfilled, (state, action) => {
        const index = state.items.findIndex(cat => cat.id === action.payload.id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
      })
      // 删除分类
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.items = state.items.filter(cat => cat.id !== action.payload);
        state.total -= 1;
      });
  },
});

export const { clearError } = categorySlice.actions;
export default categorySlice.reducer;
